import { Delete } from '@mui/icons-material';
import Print from '@mui/icons-material/Print';
import { Box, Button, Divider, Grid, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import ReactToPrint from 'react-to-print';
import DialogCustom from 'modules/common/component/DialogCustom';
import HeaderPrintForm from 'modules/common/component/HeaderPrintForm';
import { BE_DATE_FORMAT, FE_DATE_FORMAT, PATIENT_ADDRESS_TYPE, some } from 'modules/common/constants';
import { extractHTMLContent } from 'modules/common/SchemaForm/element/text-editor/TextEditorElement';
import { API_SERVER } from 'modules/common/api';
import { STORAGE_RESOURCE_TYPE } from 'modules/reception/constants';
import ImagePreview from 'modules/common/component/ImagePreview';
import { useEffect } from 'react';
import { RadioImageSize } from 'modules/common/component/RadioImageSize';
import 'modules/common/print.scss';
import { useParams } from 'react-router';
import { useFetch } from 'modules/common/hook';

const useStyles = makeStyles({
  print: {
    position: 'relative',
    height: '100%',
    display: 'block',
    padding: '24px 16px',
    '& .hiddenPage': { display: 'none' },
    [`@media print`]: {
      '& .newPage': { pageBreakBefore: 'always' },
      '& .hiddenPage': { display: 'block' },
      '& .delete-btn': { display: 'none' },
      padding: 0,
      '& th,tr,td': {
        backgroundColor: 'unset',
      },
    },
  },
});

interface FormPrintContentProps {
  therapyImage: some;
  therapy: some;
  images: some[];
}
interface Props {
  open: boolean;
  onClose: () => void;
  therapy: some;
  therapyImage: some;
}

export const FormPrintContent = (props: FormPrintContentProps) => {
  const { therapy, therapyImage, images } = props;
  const [imagesList, setImageList] = React.useState(images);

  useEffect(() => {
    setImageList(images);
  }, [images]);
  const header = React.useMemo(() => {
    return (
      <>
        <HeaderPrintForm>
          <Typography variant="h4" style={{ textTransform: 'uppercase' }}>
            <FormattedMessage id="medicalPrint.imageDiagnostic" />
          </Typography>
        </HeaderPrintForm>
        <Grid container>
          <Grid item xs={12} flexWrap={'wrap'} display="flex" columnGap={4}>
            <Box display="flex">
              <Typography variant="subtitle1">
                <FormattedMessage id="name" />
              </Typography>
              :&nbsp; <Typography variant="body1">{therapy?.patient?.name}</Typography>
            </Box>
            <Box display="flex">
              <Typography variant="subtitle1">
                <FormattedMessage id="birthdayShort" />
              </Typography>
              :&nbsp;{' '}
              <Typography variant="body1">
                {moment(therapy?.patient?.dob, BE_DATE_FORMAT).format(FE_DATE_FORMAT)}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography variant="subtitle1">
                <FormattedMessage id="gender" />
              </Typography>
              :&nbsp;{' '}
              <Typography variant="body1">
                {therapy?.patient?.gender && <FormattedMessage id={therapy?.patient?.gender?.toLowerCase()} />}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography variant="subtitle1">
                <FormattedMessage id="address" />
              </Typography>
              :&nbsp;{' '}
              <Typography variant="body1">
                {therapy.patient?.patientAddressList?.find((ele) => ele?.type === PATIENT_ADDRESS_TYPE.home)?.address}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} flexWrap={'wrap'} display="flex" columnGap={4}>
            <Box display="flex">
              <Typography variant="subtitle1">
                <FormattedMessage id="diagnosis" />
              </Typography>
              :&nbsp;{' '}
              <Typography variant="body1">
                {extractHTMLContent(therapy?.diagnosis || therapy?.provisionalDiagnosis)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  }, [therapy]);

  const content = React.useMemo(() => {
    return (
      <Box sx={{ paddingRight: 1 }}>
        <Divider sx={{ marginY: 1 }} />
        <RadioImageSize>
          {(imageSize) => (
            <Grid container spacing={1.5}>
              {imagesList?.map((item, index) => {
                return (
                  <Grid
                    key={index}
                    item
                    xs={imageSize}
                    textAlign="center"
                    display="flex"
                    flexDirection={'column'}
                    position="relative"
                  >
                    <Box>
                      <ImagePreview
                        alt=""
                        imageId={item.id}
                        style={{
                          // height: '100%',
                          width: imageSize === 12 ? '84%' : '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <Typography variant="body1" marginTop={1}>
                        {item?.description}&nbsp;
                      </Typography>

                      <IconButton
                        sx={{ position: 'absolute', top: 20, right: 8, background: 'white' }}
                        className="delete-btn"
                        onClick={() => {
                          setImageList(imagesList.filter((ele) => ele?.id !== item?.id));
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </RadioImageSize>
        <Divider sx={{ marginY: 1 }} />
        <Typography variant="h6" marginBottom={1}>
          <FormattedMessage id="description" />
        </Typography>
        <Box dangerouslySetInnerHTML={{ __html: therapyImage?.examDepartment }} />
        <Divider sx={{ marginY: 1 }} />
        <Typography variant="h6" marginBottom={1}>
          <FormattedMessage id="conclusion" />
        </Typography>
        <Box dangerouslySetInnerHTML={{ __html: therapyImage?.conclusion }} />
      </Box>
    );
  }, [imagesList, therapyImage?.examDepartment, therapyImage?.conclusion]);

  const footerSection = React.useMemo(() => {
    return (
      <>
        <Box style={{ pageBreakInside: 'avoid', marginTop: 12 }}>
          <Grid container spacing={0}>
            <Grid item xs={6} textAlign="center"></Grid>

            <Grid item xs={6} textAlign="center">
              <Typography variant="body1" fontStyle="italic">
                <FormattedMessage
                  id="printForm.date"
                  values={{
                    hour: moment().hour(),
                    min: moment().minute(),
                    day: moment().date(),
                    month: moment().month() + 1,
                    year: moment().year(),
                  }}
                />
              </Typography>
              <Typography variant="h6">
                <FormattedMessage id="examDoctor" />
              </Typography>
              <Typography variant="body1" color="textSecondary" fontStyle="italic" style={{ marginBottom: 64 }}>
                <FormattedMessage id="medicalPrint.helperText" />
              </Typography>
              <Typography variant="body1">{therapy?.picName}</Typography>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }, [therapy?.picName]);

  return (
    <>
      <Box style={{ marginBottom: 15 }}>
        {header}
        {content}
        {footerSection}
      </Box>
    </>
  );
};

const CameraPrintFormDialog = (props: Props) => {
  const { onClose, open, therapyImage, therapy } = props;
  const refPrintComponent = React.useRef(null);
  const classes = useStyles();
  const { encounterId } = useParams<{ encounterId }>();

  const { data: images } = useFetch(
    API_SERVER.storageResource.getList({
      type: encounterId
        ? STORAGE_RESOURCE_TYPE.encounterDiagNosticImageAlbum
        : STORAGE_RESOURCE_TYPE.therapyDiagnosticImageAlbum,
      referenceId: therapyImage.id,
    }),
    { enabled: therapyImage?.id },
  );

  return (
    <DialogCustom
      className="camera-print-form-dialog"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{
        style: { width: 1126 },
      }}
      title={'medicalPrint.result'}
      footer={
        <ReactToPrint
          trigger={() => (
            <Button variant="contained" color="primary" startIcon={<Print />} style={{ minWidth: 182 }}>
              <FormattedMessage id="report.imagePrint" />
            </Button>
          )}
          content={() => refPrintComponent.current}
        />
      }
    >
      {therapy && therapyImage && images && (
        <Box ref={refPrintComponent} className={classes.print}>
          <FormPrintContent therapyImage={therapyImage} therapy={therapy} images={images} />
        </Box>
      )}
    </DialogCustom>
  );
};

export default CameraPrintFormDialog;
